import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { routesMeta } from './routesMeta';
import {
  SITE_NAME,
  TITLE_SEPARATOR,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_IMAGE,
  DEFAULT_LOCALE,
  BASE_URL
} from './defaults';

// Build hreflang links; currently only Swedish + x-default for home.
function buildHreflang(pathname) {
  const origin = (BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '')) || '';
  const url = origin + pathname;
  const links = [
    { hrefLang: 'sv', href: url },
  ];
  if (pathname === '/') {
    links.push({ hrefLang: 'x-default', href: url });
  }
  return links;
}

function Seo({
  routeKey,
  title,
  description,
  image,
  keywords,
  canonical,
  noindex = false,
  robots,
  type = 'website',
  articleMeta,
  twitterSite,
  twitterCreator,
  hreflangs,
  children,
}) {
  const location = useLocation();
  const pathname = location.pathname;

  const routeMeta = routeKey ? routesMeta[routeKey] : undefined;

  const finalTitlePart = title || routeMeta?.title || DEFAULT_TITLE;
  // Avoid duplicating site name if already present
  const finalTitle = /gula\s*v[aä]ggen/i.test(finalTitlePart)
    ? finalTitlePart
    : `${finalTitlePart}${TITLE_SEPARATOR}${SITE_NAME}`;

  const finalDescription = description || routeMeta?.description || DEFAULT_DESCRIPTION;
  const finalKeywords = (keywords || routeMeta?.keywords || DEFAULT_KEYWORDS);
  const keywordContent = Array.isArray(finalKeywords) ? finalKeywords.join(', ') : finalKeywords;
  const origin = (BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '')) || '';
  const base = origin.replace(/\/$/, '');
  const rawCanonical = canonical
    ? (canonical.startsWith('http') ? canonical : base + canonical)
    : base + (routeMeta?.path || pathname);
  // Normalize duplicate slashes & ensure protocol present
  const finalCanonical = rawCanonical.replace(/([^:])\/+/g, '$1/');
  const finalImage = image || DEFAULT_IMAGE;

  const hreflangLinks = hreflangs && hreflangs.length
    ? hreflangs
    : buildHreflang(routeMeta?.path || pathname);

  return (
    <Helmet prioritizeSeoTags>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={keywordContent} />

      {/* Open Graph */}
      <meta property="og:locale" content={DEFAULT_LOCALE} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={finalImage} />
      {type === 'article' && articleMeta?.publishedTime && (
        <meta property="article:published_time" content={new Date(articleMeta.publishedTime).toISOString()} />
      )}
      {type === 'article' && articleMeta?.modifiedTime && (
        <meta property="article:modified_time" content={new Date(articleMeta.modifiedTime).toISOString()} />
      )}
      {type === 'article' && articleMeta?.section && (
        <meta property="article:section" content={articleMeta.section} />
      )}
      {type === 'article' && Array.isArray(articleMeta?.tags) && articleMeta.tags.map(t => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content={type === 'article' ? 'summary_large_image' : 'summary_large_image'} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      <link rel="canonical" href={finalCanonical} />

      {hreflangLinks.map(l => (
        <link key={l.hrefLang} rel="alternate" hrefLang={l.hrefLang} href={l.href} />
      ))}

      {(robots || noindex) && (
        <meta name="robots" content={robots ? robots : 'noindex,follow'} />
      )}

      {/* Structured Data */}
      {type === 'article' && (
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': finalCanonical,
          },
          headline: finalTitle,
          description: finalDescription,
          image: [finalImage],
          datePublished: articleMeta?.publishedTime ? new Date(articleMeta.publishedTime).toISOString() : undefined,
          dateModified: articleMeta?.modifiedTime ? new Date(articleMeta.modifiedTime).toISOString() : undefined,
          author: articleMeta?.author ? {
            '@type': 'Person',
            name: articleMeta.author,
          } : undefined,
          publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            logo: {
              '@type': 'ImageObject',
              url: finalImage,
            },
          },
          articleSection: articleMeta?.section,
          keywords: Array.isArray(articleMeta?.tags) ? articleMeta.tags.join(', ') : undefined,
        })}</script>
      )}
      {type === 'product' && articleMeta && (
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: finalTitle,
          description: finalDescription,
          image: [finalImage],
          sku: articleMeta.sku,
          brand: articleMeta.brand ? { '@type': 'Brand', name: articleMeta.brand } : undefined,
          offers: articleMeta.offers ? {
            '@type': 'Offer',
            priceCurrency: articleMeta.offers.priceCurrency,
            price: articleMeta.offers.price,
            availability: articleMeta.offers.availability,
            url: finalCanonical,
            itemCondition: articleMeta.offers.itemCondition,
          } : undefined,
        })}</script>
      )}

      {children}
    </Helmet>
  );
}

export default Seo;
